import FullCalendar from "@fullcalendar/react";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import {getLanguage} from "../../../../../locales/langUtils";
import React, {useCallback, useMemo, useRef, useState} from "react";
import {
    isDateWithinBusinessHours,
    parseEventsToMenus,
    parseMenusToEvents,
    toLocalISOString,
} from "../../../../../utils/schedulerUtils";
import {useOutsideClick} from "../../../../../hooks/useOutsideClick";
import {useSchedulerCalendarEffects} from "../../../../../hooks/useSchedulerCalendarEffects";
import {useSchedulerMenuOptions} from "../../../../../hooks/useSchedulerMenuOptions";
import {useWindowWidth} from "../../../../../hooks/useWindowWidth";
import {CalendarStickyHours} from "./CalendarStickyHours";
import {SchedulerDialogs} from "./SchedulerDialogs";
import {EventContent} from "./EventContent";
import {useBusinessHours} from "../../../../../hooks/useBusinessHours";

export const SchedulerCalendar = ({menusConfig, setMenusConfig}) => {
    const windowWidth = useWindowWidth();
    const isMobile = windowWidth <= 500;
    const scrollArea = isMobile ? 70 : 180;

    const calendarRef = useRef(null);
    const dialogRef = useRef(null);

    const scrollTargetRef = useRef(null);
    const horizontalScrollTargetRef = useRef(null);
    const dragActiveRef = useRef(false);

    const [menuPickerOpen, setMenuPickerOpen] = useState(false);
    const [openingHoursDialogOpen, setOpeningHoursDialogOpen] = useState(false);
    const [pendingSelection, setPendingSelection] = useState(null);
    const [selectedMenu, setSelectedMenu] = useState(null);

    const menuOptions = useSchedulerMenuOptions({menusConfig})
    const businessHours = useBusinessHours();

    useOutsideClick([dialogRef], () => setMenuPickerOpen(false), menuPickerOpen);
    useSchedulerCalendarEffects({
        calendarRef,
        scrollTargetRef,
        horizontalScrollTargetRef,
        dragActiveRef,
        selectedMenu,
        setSelectedMenu,
        menuOptions,
        scrollArea
    });

    const syncMenusWithCalendar = useCallback(
        (calendarApi) => {
            const allEvents = calendarApi.getEvents().map((ev) => ({
                id: ev.id,
                title: ev.title,
                start: toLocalISOString(ev.start),
                end: toLocalISOString(ev.end),
                extendedProps: ev.extendedProps,
            }));

            setMenusConfig((prevState) => parseEventsToMenus(allEvents, prevState));
        },
        [setMenusConfig]
    );

    const handleDateClick = useCallback(
        (info) => {
            if (!isDateWithinBusinessHours(info.date, businessHours)) {
                setOpeningHoursDialogOpen(true);
                return;
            }

            const start = info.date;
            const end = new Date(start.getTime() + 30 * 60 * 1000);

            setPendingSelection({
                start,
                end,
                allDay: false,
                view: info.view,
                jsEvent: info.jsEvent,
            });

            setMenuPickerOpen(true);
        },
        [businessHours]
    );

    const events = useMemo(() => parseMenusToEvents(menusConfig), [menusConfig]);

    const renderEventContent = useCallback(
        (arg) => {
            const {event, timeText} = arg;
            const start = event.start;
            const end = event.end;
            const durationMinutes = (end - start) / 60000;
            const showTitle = durationMinutes > 30;

            return (<EventContent event={event} timeText={timeText}
                                  showTitle={showTitle}
                                  syncMenusWithCalendar={syncMenusWithCalendar}
                                  calendarRef={calendarRef}/>);
        },
        [syncMenusWithCalendar]
    );

    const handleEventChange = useCallback(
        (info) => {
            syncMenusWithCalendar(info.view.calendar);
        },
        [syncMenusWithCalendar]
    );

    const eventClassNames = useCallback((arg) => {
        const {event} = arg;
        const {start, end, extendedProps} = event;
        const durationMinutes = (end - start) / 60000;

        const classes = [];

        if (durationMinutes <= 30) classes.push('fc-no-title');
        if (extendedProps?.isStandard) classes.push('fc-event-standard');

        return classes;
    }, []);

    return (
        <>
            <div className={'scheduler-calendar-modern'}>
                <div className={'calendar-scroll-x'}>
                    <div className={'calendar-inner'}>
                        <CalendarStickyHours/>
                        <div className={'fc-wrapper'}>
                            <FullCalendar ref={calendarRef}
                                          plugins={[timeGridPlugin, interactionPlugin]}
                                          height={'auto'}
                                          initialView={'timeGridWeek'}
                                          initialDate={'2020-01-06'}
                                          headerToolbar={false}
                                          allDaySlot={false}
                                          editable={true}
                                          selectable={true}
                                          firstDay={1}
                                          slotMinTime={'00:00'}
                                          slotMaxTime={'24:00'}
                                          businessHours={businessHours}
                                          eventConstraint={'businessHours'}
                                          navLinks={false}
                                          dateClick={handleDateClick}
                                          stickyHeaderDates={true}
                                          events={events}
                                          eventDrop={handleEventChange}
                                          eventResize={handleEventChange}
                                          eventResizableFromStart={true}
                                          eventLongPressDelay={300}
                                          selectLongPressDelay={300}
                                          eventChange={(changeInfo) => {
                                              syncMenusWithCalendar(changeInfo.view.calendar);
                                          }}
                                          eventDragStart={() => {
                                              dragActiveRef.current = true;
                                          }}
                                          eventDragStop={() => {
                                              dragActiveRef.current = false;
                                          }}
                                          eventResizeStart={() => {
                                              dragActiveRef.current = true;
                                          }}
                                          eventResizeStop={() => {
                                              dragActiveRef.current = false;
                                          }}
                                          dragRevertDuration={0}
                                          eventOverlap={false}
                                          selectOverlap={false}
                                          slotEventOverlap={false}
                                          slotLabelFormat={{hour: '2-digit', minute: '2-digit', hour12: false}}
                                          locale={getLanguage()}
                                          dayHeaderFormat={{weekday: 'long'}}
                                          eventClassNames={eventClassNames}
                                          eventContent={renderEventContent}
                            />
                        </div>
                    </div>
                </div>
            </div>

            <SchedulerDialogs
                openingHoursDialogOpen={openingHoursDialogOpen}
                setOpeningHoursDialogOpen={setOpeningHoursDialogOpen}
                dialogRef={dialogRef}
                setSelectedMenu={setSelectedMenu}
                selectedMenu={selectedMenu}
                menuOptions={menuOptions}
                syncMenusWithCalendar={syncMenusWithCalendar}
                pendingSelection={pendingSelection}
                setPendingSelection={setPendingSelection}
                menuPickerOpen={menuPickerOpen}
                setMenuPickerOpen={setMenuPickerOpen}
            />
        </>
    );
};